import React, { Suspense } from 'react';
import { Route, useParams } from 'react-router-dom';
import MainLayout from '@components/layout/MainLayout';
import ErrorBoundary from '@shared/components/ErrorBoundary';
import { PageSkeleton, DashboardSkeleton } from '@shared/components/ui/PageSkeleton';

// Eagerly loaded — lightweight entry points
import HotelCreationEntry from '@features/hotels/components/HotelCreationEntry';

// Lazy loaded — heavy page components
const PortfolioDashboard = React.lazy(() => import('@features/hotels/components/PortfolioDashboard'));
const PropertyDetailsTabbed = React.lazy(() => import('@features/hotels/components/PropertyDetailsTabbed'));
const HotelOCRResults = React.lazy(() => import('@features/hotels/components/HotelOCRResults'));
const HotelCreationWizard = React.lazy(() => import('@features/hotels/components/HotelCreationWizardImproved'));
const HotelImportSearch = React.lazy(() => import('@features/hotels/components/HotelImportSearch'));
const HotelBulkImport = React.lazy(() => import('@features/hotels/components/HotelBulkImport'));
const HotelQuickActions = React.lazy(() => import('@features/hotels/components/HotelQuickActions'));
const HotelSearchPage = React.lazy(() => import('@pages/HotelSearchPage'));
const HotelUSALIMappingsPage = React.lazy(() => import('@pages/HotelUSALIMappingsPage'));
const USALIStatementPage = React.lazy(() => import('@features/usali').then(m => ({ default: m.USALIStatementPage })));
const STRDashboard = React.lazy(() => import('@features/str').then(m => ({ default: m.STRDashboard })));
const ExtractionReviewPage = React.lazy(() => import('@features/financialReview/ExtractionReviewPage'));

// Wrapper component to pass route params to HotelQuickActions
const HotelQuickActionsWrapper = () => {
  const { hotelId } = useParams<{ hotelId: string }>();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <HotelQuickActions hotelId={hotelId || ''} />
    </Suspense>
  );
};

export const hotelRoutes = [
  <Route
    key="hotels"
    path="/hotels"
    element={
      <MainLayout>
        <ErrorBoundary feature="Hotel Portfolio">
          <Suspense fallback={<DashboardSkeleton />}>
            <PortfolioDashboard />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="hotel-search"
    path="/hotels/search"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <HotelSearchPage />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="hotel-details"
    path="/hotels/:hotelId"
    element={
      <MainLayout>
        <ErrorBoundary feature="Hotel Details">
          <Suspense fallback={<PageSkeleton />}>
            <PropertyDetailsTabbed />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="hotel-usali-mappings"
    path="/hotels/:hotelId/usali-mappings"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <HotelUSALIMappingsPage />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="hotel-financial-statements"
    path="/hotels/:hotelId/financial-statements"
    element={
      <MainLayout>
        <ErrorBoundary feature="Financial Statements">
          <Suspense fallback={<PageSkeleton />}>
            <USALIStatementPage />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="add-hotel"
    path="/add-hotel"
    element={
      <MainLayout>
        <HotelCreationEntry />
      </MainLayout>
    }
  />,
  <Route
    key="add-hotel-manual"
    path="/add-hotel/manual"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <HotelCreationWizard />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="add-hotel-import"
    path="/add-hotel/import"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <HotelImportSearch />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="add-hotel-bulk"
    path="/add-hotel/bulk"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <HotelBulkImport />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="hotel-quick-actions"
    path="/hotels/:hotelId/quick-actions"
    element={
      <MainLayout>
        <HotelQuickActionsWrapper />
      </MainLayout>
    }
  />,
  <Route
    key="hotel-ocr-results"
    path="/hotel-ocr-results"
    element={
      <MainLayout>
        <ErrorBoundary feature="OCR Results">
          <Suspense fallback={<PageSkeleton />}>
            <HotelOCRResults />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="hotel-str-analysis"
    path="/hotels/:hotelId/str-analysis"
    element={
      <MainLayout>
        <ErrorBoundary feature="STR Analysis">
          <Suspense fallback={<PageSkeleton />}>
            <STRDashboard />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="extraction-review"
    path="/hotels/:hotelId/financial-review/:fileId"
    element={
      <MainLayout>
        <ErrorBoundary feature="Financial Review">
          <Suspense fallback={<PageSkeleton />}>
            <ExtractionReviewPage />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />
];

export default hotelRoutes;
